import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { ApiError, configureApi, NetworkError } from '../lib/api.ts';
import { fetchSession, SESSION_KEY } from '../lib/session.ts';
import { ToastProvider } from '../lib/toast.tsx';

/*
 * One place for session and error policy:
 *   401             -> the session ended: forget it (RequireSession sends to the login)
 *   403 mfa_required -> reload the session (RequireSession sends to the 2FA step)
 *   network errors  -> retried up to 3 times with backoff; 4xx never retried
 */
export function createQueryClient(): QueryClient {
  const client: QueryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error) => {
        handleAuthErrors(client, error);
      },
    }),
    mutationCache: new MutationCache({
      onError: (error) => {
        handleAuthErrors(client, error);
      },
    }),
    defaultOptions: {
      queries: {
        retry: (count, error) =>
          (error instanceof NetworkError || (error instanceof ApiError && error.status >= 500)) &&
          count < 3,
        refetchOnWindowFocus: true,
      },
      mutations: { retry: false },
    },
  });
  configureApi({
    refreshCsrf: async () => {
      const session = await fetchSession();
      client.setQueryData(SESSION_KEY, session);
      return session?.csrfToken ?? null;
    },
  });
  return client;
}

function handleAuthErrors(client: QueryClient, error: unknown): void {
  if (!(error instanceof ApiError)) return;
  if (error.status === 401) client.setQueryData(SESSION_KEY, null);
  if (error.status === 403 && error.code === 'mfa_required')
    void client.invalidateQueries({ queryKey: SESSION_KEY });
}

export function Providers({ client, children }: { client: QueryClient; children: ReactNode }) {
  return (
    <QueryClientProvider client={client}>
      <ToastProvider>{children}</ToastProvider>
    </QueryClientProvider>
  );
}
